<template>
  <div class="mb-3">
    <div v-if="midiAccess" class="form-floating">
      <select id="biotron-device" v-model.number="currentMidiNum" class="form-control" @change="deviceChanged" :disabled="released || connecting || !midiAccess">
        <option v-for="(device, key) in devices" :key="device.output.id" :value="key">
          {{ device.output.name }} {{ versions[device.output.id] }}
        </option>
      </select>
      <label for="biotron-device">{{ text_label }}</label>
    </div>
    <p v-if="connectionStatusMessage" class="connection-status mt-2 mb-0" :class="connectionStatusClass" role="status" aria-live="polite">
      {{ connectionStatusMessage }}
    </p>
    <div v-if="allowDawHandoff && (selectedDevice || released)" class="daw-handoff d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center mt-2">
      <button v-if="!released" type="button" class="btn btn-outline-primary daw-handoff__button" @click="releaseMidi" :disabled="connecting || !selectedDevice">
        Release for another app
      </button>
      <button v-else type="button" class="btn btn-primary daw-handoff__button" @click="connectMidi" :disabled="connecting">
        Reconnect
      </button>
    </div>
    <button
        v-if="!released && !selectedDevice && (!midiAccess || midiError)"
        type="button"
        class="btn btn-primary mt-2"
        @click="connectMidi"
        :disabled="connecting"
    >
      {{ midiError ? "Retry connection" : "Connect Biotron" }}
    </button>
    <div v-if="midiError" class="alert alert-warning mt-2 mb-0" role="alert">{{ midiError }}</div>
  </div>
</template>

<script>
  import {parseBiotronSettingsReply} from "@/assets/js/BiotronSettingsReadback";
  import {isReadOnlyBiotronInspectorRequest} from "@/assets/js/BiotronSafeInspector";

  const portIdentity = (port) => [port.manufacturer || "", port.name || ""].join("\u0000");
  let midiAccessPromise = null;

  const requestMidiAccess = () => {
    if (!navigator.requestMIDIAccess) {
      return Promise.reject(new Error("Web MIDI is not supported by this browser."));
    }
    if (!midiAccessPromise) {
      midiAccessPromise = navigator.requestMIDIAccess({sysex: true}).catch((error) => {
        midiAccessPromise = null;
        throw error;
      });
    }
    return midiAccessPromise;
  };

  export default {
    props: {
      regexName: {
        default: ".*",
        type: String
      },
      checkVersionsFlag: {
        default: false,
        type: Boolean
      },
      allowDawHandoff: {
        default: false,
        type: Boolean
      },
      text_label: {
        default: "Select device",
        type: String,
      },
    },
    emits: ["device_changed", "firmware_version_changed", "settings_readback", "midi_activity"],
    name: "DeviceSelector",
    data() {
      return {
        devices: [],
        versions: {},
        currentMidiNum: 0,
        updateTimeout: null,
        midiAccess: null,
        selectedDevice: null,
        released: false,
        connecting: false,
        midiError: "",
        operationId: 0,
        unmounted: false,
        inspectionRequestId: 0
      }
    },
    computed: {
      connectionStatusMessage() {
        if (this.connecting) return "Connecting…";
        if (this.released) return "Released — another app can use Biotron.";
        if (this.selectedDevice) {
          const output = this.selectedDevice.output;
          const version = this.versions[output.id];
          return `Connected · ${output.name}${version ? ` · ${version}` : ""}`;
        }
        return this.midiAccess ? "Biotron not found. Check the USB data cable." : "";
      },
      connectionStatusClass() {
        if (this.released) return "connection-status--released";
        if (this.selectedDevice && !this.connecting) return "connection-status--connected";
        return "connection-status--idle";
      }
    },
    methods: {
      matchingPorts(ports) {
        return [...ports.values()].filter((port) => port.name && port.name.match(this.regexName));
      },
      pairDevices(midi) {
        const inputsByIdentity = new Map();
        for (const input of this.matchingPorts(midi.inputs)) {
          const identity = portIdentity(input);
          if (!inputsByIdentity.has(identity)) inputsByIdentity.set(identity, []);
          inputsByIdentity.get(identity).push(input);
        }

        return this.matchingPorts(midi.outputs).map((output) => {
          const matchingInputs = inputsByIdentity.get(portIdentity(output)) || [];
          return {output, input: matchingInputs.shift()};
        });
      },
      hasAmbiguousIdentity(devices) {
        const counts = new Map();
        for (const device of devices) {
          const identity = portIdentity(device.output);
          counts.set(identity, (counts.get(identity) || 0) + 1);
        }
        return [...counts.values()].some((count) => count > 1);
      },
      midiReady(midi) {
        this.midiAccess = midi;
        midi.onstatechange = () => {
          if (!this.released && !this.connecting) this.refreshDevices();
        };
        return this.refreshDevices();
      },
      async connectMidi() {
        const operationId = ++this.operationId;
        this.connecting = true;
        this.midiError = "";
        try {
          const midi = this.midiAccess || await requestMidiAccess();
          if (operationId !== this.operationId || this.unmounted) return;
          this.released = false;
          await this.midiReady(midi);
        } catch (err) {
          if (operationId === this.operationId && !this.unmounted) {
            if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
              this.midiError = "The browser did not allow access to USB MIDI devices. Allow it in the browser prompt, then retry.";
            } else if (!navigator.requestMIDIAccess) {
              this.midiError = "This browser cannot connect to USB MIDI devices. Use current Chrome or Edge on a desktop computer.";
            } else {
              this.midiError = "Biotron may be open in another app. Close other MIDI apps, then retry.";
            }
          }
          console.log('Something went wrong', err);
        } finally {
          if (operationId === this.operationId && !this.unmounted) this.connecting = false;
        }
      },
      clearUpdateTimeout() {
        if (this.updateTimeout !== null) clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      },
      readOnlyInspectorSend(output, message) {
        if (!isReadOnlyBiotronInspectorRequest(message)) {
          throw new Error("Safe Inspector refused a non-read-only MIDI request.");
        }
        output.send(message);
      },
      waitForInspectorReply(input, parser, timeoutMs = 1800) {
        return new Promise((resolve) => {
          let finished = false;
          const finish = (value) => {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            input.onmidimessage = null;
            resolve(value);
          };
          const timeout = setTimeout(() => finish(null), timeoutMs);
          input.onmidimessage = (event) => {
            const value = parser(Array.from(event.data || []));
            if (value) finish(value);
          };
        });
      },
      parseInspectorVersion(bytes, nonce) {
        if (bytes.length !== 8 || bytes[0] !== 0xf0 || bytes[1] !== 0x0b ||
            bytes[2] !== 126 || bytes[3] !== nonce || bytes[7] !== 0xf7 ||
            bytes.slice(4, 7).some(byte => !Number.isInteger(byte) || byte < 0 || byte > 0x7f)) return null;
        return `${bytes[4]}.${bytes[5]}.${bytes[6]}`;
      },
      async inspectAllPortsReadOnly({sessionId, deviceEpoch}) {
        const result = (status, reason, ports = []) => ({
          sessionId, deviceEpoch, status, reason, ports
        });
        if (this.released || this.connecting || !this.selectedDevice || !this.midiAccess) {
          return result("blocked", "device_not_ready");
        }
        if (this.hasAmbiguousIdentity(this.devices)) return result("blocked", "ambiguous_device_identity");
        if (this.devices.length !== 2 || this.devices.some(device => !device.input || !device.output)) {
          return result("blocked", "expected_two_matched_midi_pairs");
        }

        const originalDevice = this.selectedDevice;
        const originalIndex = this.devices.findIndex(device => device.output.id === originalDevice.output.id);
        if (originalIndex < 0) return result("blocked", "selected_device_missing");

        const operationId = ++this.operationId;
        this.connecting = true;
        this.clearUpdateTimeout();
        this.midiError = "";
        const ports = [];
        let failure = null;

        const closeOriginal = await this.closeDevice(originalDevice);
        if (closeOriginal.length) failure = `close_failed:${[...new Set(closeOriginal)].join(",")}`;

        try {
          for (let index = 0; !failure && index < this.devices.length; index++) {
            const device = this.devices[index];
            try {
              await device.output.open();
              await device.input.open();
              if (operationId !== this.operationId || this.unmounted) {
                failure = "session_changed";
                break;
              }

              const versionNonce = ++this.inspectionRequestId & 0x7f;
              const versionReply = this.waitForInspectorReply(
                device.input, bytes => this.parseInspectorVersion(bytes, versionNonce)
              );
              this.readOnlyInspectorSend(device.output, [0xf0, 0x14, 0x0d, 126, versionNonce, 0xf7]);
              const firmware = await versionReply;
              if (!firmware) {
                failure = `firmware_timeout:port_${index + 1}`;
                continue;
              }

              const settingsNonce = ++this.inspectionRequestId & 0x7f;
              const settingsReply = this.waitForInspectorReply(device.input, bytes => {
                const reply = parseBiotronSettingsReply(bytes);
                return reply && reply.requestId === settingsNonce ? reply : null;
              });
              this.readOnlyInspectorSend(device.output, [0xf0, 0x14, 0x0d, 123, 1, settingsNonce, 0xf7]);
              const settings = await settingsReply;
              if (!settings) {
                failure = `settings_timeout:port_${index + 1}`;
                continue;
              }

              ports.push({
                label: device.output.name || `Biotron MIDI port ${index + 1}`,
                firmware,
                settingsClean: settings.source === 1 && settings.sourceValid && !settings.dirty,
                dirtyGeneration: settings.dirtyGeneration,
                persistedGeneration: settings.persistedGeneration,
                settingsFingerprint: settings.values.join(",")
              });
              this.versions[device.output.id] = `v${firmware}`;
            } catch (error) {
              failure = `probe_failed:port_${index + 1}`;
              console.log("Safe Inspector port probe failed", error);
            } finally {
              const closeFailures = await this.closeDevice(device);
              if (!failure && closeFailures.length) {
                failure = `probe_close_failed:${[...new Set(closeFailures)].join(",")}`;
              }
            }
          }
        } finally {
          if (operationId === this.operationId && !this.unmounted) {
            try {
              await originalDevice.output.open();
              await originalDevice.input.open();
              originalDevice.input.onmidimessage = event => this.handleMidiMessage(event, operationId);
              this.selectedDevice = originalDevice;
              this.currentMidiNum = originalIndex;
            } catch (error) {
              failure = "reconnect_failed";
              this.selectedDevice = null;
              this.$emit("device_changed", undefined);
              this.midiError = "The check finished, but this page could not reconnect to Biotron. Close other music apps, then select Reconnect.";
            }
            this.connecting = false;
          }
        }

        if (operationId !== this.operationId || this.unmounted) return result("blocked", "session_changed");
        return failure ? result("fail", failure, ports) : result("pass", "fresh_read_only_replies", ports);
      },
      async closeDevice(device) {
        if (!device) return [];
        const failures = [];
        if (device.input) device.input.onmidimessage = null;
        for (const port of [device.input, device.output].filter(Boolean)) {
          try {
            await port.close();
          } catch (err) {
            failures.push(port.name);
            console.log('Could not close MIDI port', err);
          }
        }
        return failures;
      },
      async releaseMidi() {
        const operationId = ++this.operationId;
        this.connecting = true;
        this.clearUpdateTimeout();
        this.midiError = "";
        if (this.midiAccess) this.midiAccess.onstatechange = null;

        const device = this.selectedDevice;
        const failures = await this.closeDevice(device);
        if (operationId !== this.operationId) return;
        this.connecting = false;

        if (failures.length) {
          this.midiError = `Settings could not free: ${[...new Set(failures)].join(", ")}. Retry, or close this page before opening your music app.`;
          this.released = false;
          this.$emit("device_changed", undefined);
          return;
        }

        this.selectedDevice = null;
        this.released = true;
        this.$emit("device_changed", undefined);
      },
      async refreshDevices() {
        if (!this.midiAccess || this.released) return;
        const previousOutputId = this.selectedDevice && this.selectedDevice.output.id;
        this.devices = this.pairDevices(this.midiAccess);
        if (this.hasAmbiguousIdentity(this.devices)) {
          this.clearUpdateTimeout();
          const failures = await this.closeDevice(this.selectedDevice);
          if (failures.length) {
            this.$emit("device_changed", undefined);
            this.midiError = `More than one Biotron is connected, and ${[...new Set(failures)].join(", ")} did not close. Disconnect the extra Biotron, then retry.`;
            this.connecting = false;
            return;
          }
          this.selectedDevice = null;
          this.$emit("device_changed", undefined);
          this.midiError = "More than one Biotron is connected. Disconnect the others so Settings cannot change the wrong unit.";
          this.connecting = false;
          return;
        }
        if (!this.devices.length) {
          this.selectedDevice = null;
          this.$emit("device_changed", undefined);
          this.midiError = "Biotron was not found. Use a USB data cable, reconnect it, then retry.";
          this.connecting = false;
          return;
        }
        const previousIndex = this.devices.findIndex((device) => device.output.id === previousOutputId);
        if (previousIndex >= 0) this.currentMidiNum = previousIndex;
        else if (!this.devices[this.currentMidiNum]) this.currentMidiNum = 0;
        await this.deviceChanged();
      },
      async deviceChanged() {
        if (this.released) return;
        const operationId = ++this.operationId;
        this.connecting = true;
        this.clearUpdateTimeout();
        this.midiError = "";

        const previousDevice = this.selectedDevice;
        const nextDevice = this.devices[this.currentMidiNum];
        if (previousDevice && (!nextDevice || previousDevice.output.id !== nextDevice.output.id)) {
          const failures = await this.closeDevice(previousDevice);
          if (operationId !== this.operationId || this.unmounted) return;
          if (failures.length) {
            const previousIndex = this.devices.findIndex((device) => device.output.id === previousDevice.output.id);
            if (previousIndex >= 0) this.currentMidiNum = previousIndex;
            this.connecting = false;
            this.$emit("device_changed", undefined);
            this.midiError = `Settings could not disconnect from: ${[...new Set(failures)].join(", ")}. Retry, or close this page.`;
            return;
          }
        }
        if (operationId !== this.operationId || this.unmounted) return;

        this.selectedDevice = nextDevice || null;
        if (!nextDevice) {
          this.connecting = false;
          this.$emit("device_changed", undefined);
          return;
        }

        try {
          await nextDevice.output.open();
          if (nextDevice.input) {
            await nextDevice.input.open();
            if (operationId !== this.operationId || this.unmounted) {
              await this.closeDevice(nextDevice);
              return;
            }
            nextDevice.input.onmidimessage = (event) => this.handleMidiMessage(event, operationId);
          }
          if (operationId !== this.operationId || this.unmounted) {
            await this.closeDevice(nextDevice);
            return;
          }
          this.$emit("device_changed", nextDevice.output);
          this.scheduleVersionQuery(nextDevice, operationId);
        } catch (err) {
          await this.closeDevice(nextDevice);
          if (operationId === this.operationId && !this.unmounted) {
            this.selectedDevice = null;
            this.$emit("device_changed", undefined);
            this.midiError = "Could not connect. Another MIDI app may be using Biotron.";
          }
          console.log('Could not open MIDI port', err);
        } finally {
          if (operationId === this.operationId && !this.unmounted) this.connecting = false;
        }
      },
      scheduleVersionQuery(device, operationId) {
        if (!this.checkVersionsFlag) return;
        const knownVersion = this.versions[device.output.id];
        if (knownVersion) {
          this.$emit("firmware_version_changed", knownVersion.startsWith("v") ? knownVersion.slice(1) : null);
          return;
        }
        this.versions[device.output.id] = "checking firmware…";
        this.$emit("firmware_version_changed", null);
        this.updateTimeout = setTimeout(() => {
          this.updateTimeout = null;
          if (operationId !== this.operationId || this.released || this.selectedDevice !== device) return;
          try {
            device.output.send([240, 20, 13, 126, this.currentMidiNum, 247]);
            this.updateTimeout = setTimeout(() => {
              this.updateTimeout = null;
              if (operationId !== this.operationId || this.released || this.selectedDevice !== device) return;
              if (this.versions[device.output.id] === "checking firmware…") {
                this.versions[device.output.id] = "firmware version unavailable";
              }
            }, 1500);
          } catch (err) {
            if (operationId === this.operationId) this.midiError = "Biotron connected, but did not return its firmware version.";
          }
        }, 3000);
      },
      handleMidiMessage(event, operationId) {
        if (operationId !== this.operationId || this.released) return;
        const settingsReply = parseBiotronSettingsReply(event.data);
        if (settingsReply) {
          this.$emit("settings_readback", settingsReply);
          return;
        }
        const [start_sys_ex, flag_byte, num_com, id_of_output, x, y, z, end_sys_ex] = event.data;
        if (start_sys_ex === 0xF0 && end_sys_ex === 0xF7 && flag_byte === 0x0B &&
            num_com === 126 && event.data.length === 8 && id_of_output === this.currentMidiNum) {
          this.clearUpdateTimeout();
          const version = `${x}.${y}.${z}`;
          this.versions[this.selectedDevice.output.id] = `v${version}`;
          this.$emit("firmware_version_changed", version);
          return;
        }
        const status = event.data[0];
        const type = status & 0xf0;
        if (status < 0xf0 && event.data.length >= 3) {
          this.$emit("midi_activity", {
            type: type === 0x90 && event.data[2] > 0 ? "note-on" :
              type === 0x80 || (type === 0x90 && event.data[2] === 0) ? "note-off" :
                type === 0xe0 ? "pitch-bend" : type === 0xb0 ? "control-change" : "channel-message",
            channel: (status & 0x0f) + 1,
            note: event.data[1],
            velocity: event.data[2],
            value: event.data[1] + (event.data[2] << 7)
          });
        }
      }
    },
    beforeUnmount() {
      this.unmounted = true;
      ++this.operationId;
      this.clearUpdateTimeout();
      if (this.midiAccess) this.midiAccess.onstatechange = null;
      // An in-flight lifecycle operation observes operationId and closes its
      // device. Avoid racing it with a second close from the unmount hook.
      if (!this.connecting) this.closeDevice(this.selectedDevice);
    }
  }
</script>

<style scoped>
.connection-status {
  min-height: 1.35rem;
  font-size: .9rem;
  text-align: left;
}
.connection-status--connected { color: #146c43; }
.connection-status--released { color: #0d6efd; }
.connection-status--idle { color: #6c757d; }

.daw-handoff__button {
  min-width: 12rem;
  min-height: 44px;
}

@media (max-width: 575.98px) {
  .daw-handoff__button {
    width: 100%;
    min-width: 0;
  }
}
</style>
